import * as Types from '@aeppic/types'
import BTreeModule from 'sorted-btree'
const BTree: typeof BTreeModule = (BTreeModule as any).default ?? BTreeModule
import { DocumentIdIterator, IIndex } from './index-interface'
import { resolveField, IndexOptions } from './document-index'

export class BTreeIndex implements IIndex {
  private _valuesToDocumentIds: BTreeModule<any, BTreeModule<Types.DocumentId, null>> = new BTree()
  private _documentIdsToValues: BTreeModule<Types.DocumentId, any[]> = new BTree()
  private _isSuperSet = false
  private _prefixLength: number | null = null

  constructor(
    public fieldPath: string,
    private _options: IndexOptions = {}
  ) {
    if ('type' in _options && _options.type === 'prefix') {
      this._isSuperSet = true
      this._prefixLength = _options.length
    }
  }

  get prefixLength() { return this._prefixLength }
  get isSuperSet() { return this._isSuperSet }

  size(): number { return this._valuesToDocumentIds.size }
  entryCount(): number { return this._documentIdsToValues.size }

  addIfMatches(entry: Types.Document): void {
    if (this._options.inclusionFilter && !this._options.inclusionFilter(entry)) return

    const fieldValue = resolveField(entry, this.fieldPath)

    if (fieldValue != null && (typeof fieldValue === 'string' ? fieldValue !== '' : true)) {
      this._add(fieldValue, entry)
    }
  }

  private _add(value: any, entry: Types.Document): void {
    const mappedValues = Array.isArray(value) ? value.map(v => this._mapValue(v)) : [this._mapValue(value)]
    for (const mappedValue of mappedValues) {
      this._addSingleValue(mappedValue, entry)
    }
    this._documentIdsToValues.set(entry.id, mappedValues)
  }

  private _addSingleValue(mappedValue: any, entry: Types.Document): void {
    if (mappedValue == null) {
      return
    }

    const list = this._getEntryList(mappedValue)
    list.set(entry.id, null)
  }

  has(id: string): boolean { return this._documentIdsToValues.has(id) }

  clear() {
    this._valuesToDocumentIds.clear()
    this._documentIdsToValues.clear()
  }

  mappedValues(): IterableIterator<any> {
    return this._valuesToDocumentIds.keys()
  }

  remove(id: string): void {
    const values = this._documentIdsToValues.get(id)

    this._documentIdsToValues.delete(id)
    
    if (values) {
      for (const fullValue of values) {
        const mappedValue = this._mapValue(fullValue)
        this._removeSingleValue(mappedValue, id)
      }
    }
  }

  private _removeSingleValue(mappedValue: any, documentId: string): void {
    const directEntries = this._valuesToDocumentIds.get(mappedValue)

    if (directEntries) {
      directEntries.delete(documentId)

      if (directEntries.size === 0) {
        this._valuesToDocumentIds.delete(mappedValue)
      } 
    }
  }

  getDocumentIdsForValue(value: any): DocumentIdIterator {
    if (Array.isArray(value)) {
      throw new Error('BTreeIndex.getDocumentIdsForValue should never receive array values - arrays must be unpacked by iterator-builders')
    }

    const mappedValue = this._mapValue(value)

    if (mappedValue == null) {
      return new DocumentIdIterator(new BTree(), true)
    }

    const entries = this._valuesToDocumentIds.get(mappedValue)

    return new DocumentIdIterator(entries || new BTree(), true)
  }

  getEntriesForRange(minValue: any, maxValue: any): Array<[key: any, entries: DocumentIdIterator]> {
    const mappedMin = this._mapValue(minValue)
    const mappedMax = this._mapValue(maxValue)

    const matchingEntries: Array<[key: any, entries: DocumentIdIterator]> = []

    const entry: [any, BTreeModule<Types.DocumentId, null>] = [undefined, undefined]
    const iterator = this._valuesToDocumentIds.entries(mappedMin, entry)

    for (;;) {
      const { done } = iterator.next()
      
      if (done) {
        break
      }

      const currentKey = entry[0]
      const currentSet = entry[1]

      if (mappedMax != null && currentKey > mappedMax) {
        break
      }

      if (currentSet && currentSet.size > 0) {
        matchingEntries.push([currentKey, new DocumentIdIterator(currentSet, true)])
      }
    }
    return matchingEntries
  }

  private _getEntryList(mappedValue: any): BTreeModule<Types.DocumentId, null> {
    let entries = this._valuesToDocumentIds.get(mappedValue)

    if (!entries) {
      entries = new BTree<Types.DocumentId, null>()
      this._valuesToDocumentIds.set(mappedValue, entries)
    }

    return entries
  }

  private _mapValue(value: any): any {
    if (value == null) {
      return null
    }

    if (typeof value === 'number'){
      if (isNaN(value)) {
        return null
      }
      return value
    }

    if (typeof value !== 'string') {
      value = value.toString()
    }

    if ('type' in this._options && this._options.type === 'prefix') {
      return value.length > this._options.length ? value.substr(0, this._options.length).toLowerCase() : value.toLowerCase()
    }

    return value
  }
}
